/**
 * DIEUKYDIEU.TV - WEB STORY API SERVICE
 * Provides asynchronous data fetching for home carousel stories & modern web story slides.
 * Supports transparent mock mode and live backend REST endpoints.
 * Under 300 lines rule compliant.
 */

import { API_CONFIG } from './config.js';
import { apiClient } from './client.js';
import { mockAdapter } from './mock/mock-adapter.js';

export const storyApi = {
    /**
     * Get list of home carousel story topics
     * @returns {Promise<Array>}
     */
    async getStoryTopics() {
        if (API_CONFIG.USE_MOCK) {
            const res = await mockAdapter.getStoryTopics();
            return res.data;
        }

        try {
            const res = await apiClient.get('/stories/topics');
            return res.data || res;
        } catch (err) {
            console.warn('[storyApi] Failed fetching story topics, falling back to mock:', err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                const res = await mockAdapter.getStoryTopics();
                return res.data;
            }
            throw err;
        }
    },

    /**
     * Get story slides and metadata for a specific topic
     * @param {string} storyId
     * @param {string} [lang='vi']
     * @returns {Promise<Object>}
     */
    async getStoryById(storyId, lang = 'vi') {
        if (API_CONFIG.USE_MOCK) {
            const res = await mockAdapter.getWebStory(storyId, lang);
            return res.data;
        }

        try {
            const res = await apiClient.get(`/stories/${encodeURIComponent(storyId)}`, { lang });
            return res.data || res;
        } catch (err) {
            console.warn(`[storyApi] Failed fetching story ${storyId}, falling back to mock:`, err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                const res = await mockAdapter.getWebStory(storyId, lang);
                return res.data;
            }
            throw err;
        }
    },

    /**
     * Record a story view or CTA click for analytics
     * @param {string} storyId
     * @param {string} action
     */
    async trackStoryEvent(storyId, action = 'view') {
        if (API_CONFIG.USE_MOCK) {
            return { success: true, storyId, action };
        }

        try {
            return await apiClient.post('/stories/analytics', { storyId, action });
        } catch (err) {
            console.warn('[storyApi] Failed recording story analytics:', err.message);
            return { success: false, error: err.message };
        }
    }
};
